/**
 * Progressive Gallery Hydration
 *
 * Converts lightweight stub slides (rendered server-side as `.main-product-gallery__item--stub`)
 * into full DOM slides that Swiper + other gallery code expects. Runs after LCP paint to keep
 * the main thread free during the critical render window.
 *
 * Flow:
 *   1. Server renders hero slide in full + N-1 stubs with data attributes only
 *   2. LCP fires (hero image paints)
 *   3. This module hydrates all stubs into full slides
 *   4. Dispatches `galleryHydrated` event — consumers (Swiper init, thumbnail clone, user groups filter) listen for this
 *
 * Public API:
 *   window.Branch.productGallery.hydrateStubs()            — hydrate all stubs now
 *   window.Branch.productGallery.hydrateStubsByMediaIds()  — hydrate specific stubs (used by variant change)
 *   window.Branch.productGallery.hydrationState            — 'pending' | 'hydrated'
 */
(function() {
  'use strict';

  window.Branch = window.Branch || {};
  window.Branch.productGallery = window.Branch.productGallery || {};
  window.Branch.productGallery.hydrationState = 'pending';

  var SRCSET_WIDTHS = [90, 180, 360, 540, 720, 900, 1080, 1280];

  // Brand color for the play icon, matches settings.content_text_color used by
  // the icons.liquid snippet when it renders inside the gallery video slide.
  // If the brand color ever changes, update both this constant and the matching
  // value in config/settings_data.json.
  var PLAY_ICON_FILL = '#314438';

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function(c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function buildSrcset(baseUrl) {
    if (!baseUrl) return '';
    return SRCSET_WIDTHS.map(function(w) {
      return baseUrl.replace(/width=\d+/, 'width=' + w) + ' ' + w + 'w';
    }).join(', ');
  }

  function buildImageSlideInnerHTML(src90, src540, src1800, alt, hasLazyBg, activeCampaignPromoStylingEnabled, activeCampaignDefaultSwatchStylingEnabled, mfSwatchText, resolvedSaleText) {
    var srcset = buildSrcset(src540);
    var sizes = '(max-width: 600px) 100vw, (max-width: 900px) calc(100vw - 48px), (max-width: 1167px) calc(((100vw - 48px)) - 490px), (max-width: 1259px) calc(((100vw - 84px)) * 0.55), 648px';
    var altEsc = escapeHtml(alt);
    var bgAttr = hasLazyBg ? ' data-lazy-bg="' + escapeHtml(src1800) + '"' : '';
    var promoAttr = activeCampaignPromoStylingEnabled ? ' active-campaign-promo-style="true" active-campaign-promo-type="discount_swatches"' : '';
    var campaignSwatchBg = activeCampaignDefaultSwatchStylingEnabled ? ' data-display-active-campaign-swatch-background="true" ' : '';
    var mfSwatchValue = mfSwatchText ? ` data-mf-swatch-text="${mfSwatchText}" ` : '';
    // Pre-populate the .sale node from the resolved message so the badge is visible the moment the
    // slide hydrates; a later saleTagUpdate re-run stays idempotent (same text + sale--display).
    var saleClass = resolvedSaleText ? 'sale sale--display' : 'sale';
    var saleText = resolvedSaleText ? escapeHtml(resolvedSaleText) : '';
    return (
      '<div class="main-product-gallery__image-container swiper-zoom-container">' +
        '<div class="main-product-gallery__image-inner-container"' + bgAttr + '>' +
          '<div class="main-product-gallery__bg-image">' +
            '<img src="' + escapeHtml(src90) + '" data-lazy-src="' + escapeHtml(src540) + '" loading="lazy">' +
          '</div>' +
          '<img src="' + escapeHtml(src90) + '"' +
            ' data-lazy-src="' + escapeHtml(src540) + '"' +
            ' data-lazy-srcset="' + escapeHtml(srcset) + '"' +
            ' class="main-product__main-product-image"' +
            ' width="540" height="540"' +
            ' sizes="' + sizes + '"' +
            ' alt="' + altEsc + '"' +
            ' loading="lazy">' +
          '<div' + promoAttr + campaignSwatchBg + mfSwatchValue + ' class="' + saleClass + '">' + saleText + '</div>' +
        '</div>' +
      '</div>'
    );
  }

  function buildVideoSlideInnerHTML(mediaId, aspectRatioMultiplier, previewSrc) {
    // Aspect-ratio wrapper mirrors the Liquid output in main-product-gallery.liquid
    var padding = 100 - aspectRatioMultiplier;
    return (
      '<div class="main-product-gallery__video-container"' +
        ' data-video-type="video"' +
        ' style="width: 100%; height: 0; padding-bottom: calc(100% - ' + padding + '%); position: relative;">' +
        '<deferred-media class="deferred-media" data-media-id="' + escapeHtml(mediaId) + '">' +
          '<button id="Deferred-Poster-' + escapeHtml(mediaId) + '" class="deferred-media__poster" type="button">' +
            (previewSrc
              ? '<img src="' + escapeHtml(previewSrc) + '" loading="lazy" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover;">'
              : ''
            ) +
          '</button>' +
          // Note: <template> element contents for deferred-media need to be set via innerHTML on the template
        '</deferred-media>' +
        '<div class="custom-video-play" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 10;">' +
          // Play SVG mirrors {% render 'icons', icon: 'play' %} in main-product-gallery.liquid.
          // Without it the button renders as an empty white circle and the click handler
          // wired up to .custom-video-play never sees a target.
          '<div class="custom-video-play-button" role="button" aria-label="Play" style="pointer-events: none;">' +
            '<svg aria-hidden="true" role="presentation" class=" icon icon-play" viewBox="0 0 32 32">' +
              '<path fill="' + PLAY_ICON_FILL + '" d="M6.752,0.055l21.904,15.944L6.754,31.945"/>' +
            '</svg>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  /**
   * Convert a single stub into a full slide in place.
   * Returns the new slide element.
   */
  function hydrateStub(stub) {
    if (!stub || !stub.classList.contains('main-product-gallery__item--stub')) return stub;

    var mediaId = stub.getAttribute('data-media-id') || '';
    var mediaType = stub.getAttribute('data-media-type') || 'image';
    var src90 = stub.getAttribute('data-preview-src-90') || '';
    var src540 = stub.getAttribute('data-preview-src-540') || '';
    var src1800 = stub.getAttribute('data-preview-src-1800') || '';
    var alt = stub.getAttribute('data-preview-alt') || '';
    var aspectRatioAttr = stub.getAttribute('data-aspect-ratio-multiplier');
    var aspectRatioMultiplier = aspectRatioAttr ? parseFloat(aspectRatioAttr) : 100;
    // Every slide keeps its 1800px zoom image URL in data-lazy-bg. The zoom overlay's clones are
    // the only place it is applied (loadGalleryImage with origin 'zoom'), so it costs nothing on a
    // gallery slide. This used to be the opposite of "the stub is slideVisible", which left
    // exactly the slides of the selected variant without one whenever the variant was filtered
    // before hydration ran, and their zoom then had no high-resolution image.
    var hasLazyBg = !!src1800;

    var gallery = stub.closest('.main-product-gallery');
    var activeCampaignPromoStylingEnabled = gallery ? gallery.getAttribute('data-active-campaign-promo-style-enabled') === 'true' : false;
    var activeCampaignDefaultSwatchStylingEnabled = gallery ? gallery.getAttribute('data-active-campaign-swatch-default-background-enabled') === 'true' : false;
    var mfSwatchText = gallery && gallery.getAttribute('data-mf-swatch-text') && gallery.getAttribute('data-mf-swatch-text') !== '' ? gallery.getAttribute('data-mf-swatch-text') : false;
    // Resolved sale message stamped by saleTagUpdate for the current variant. When present, build
    // the .sale node already populated so late-hydrated slides show the badge without waiting for
    // a saleTagUpdate re-run.
    var resolvedSaleText = gallery && gallery.getAttribute('data-resolved-sale-text') && gallery.getAttribute('data-resolved-sale-text') !== '' ? gallery.getAttribute('data-resolved-sale-text') : false;

    var innerHTML;
    if (mediaType === 'image') {
      innerHTML = buildImageSlideInnerHTML(src90, src540, src1800, alt, hasLazyBg, activeCampaignPromoStylingEnabled, activeCampaignDefaultSwatchStylingEnabled, mfSwatchText, resolvedSaleText);
    } else if (mediaType === 'video' || mediaType === 'external_video') {
      innerHTML = buildVideoSlideInnerHTML(mediaId, aspectRatioMultiplier, src540);
    } else {
      innerHTML = '';
    }

    // Strip the stub marker class, keep everything else
    stub.classList.remove('main-product-gallery__item--stub');
    stub.removeAttribute('data-hydration-stub');
    stub.innerHTML = innerHTML;
    return stub;
  }

  function hydrateStubs() {
    if (window.Branch.productGallery.hydrationState === 'hydrated') return;
    var stubs = document.querySelectorAll('.main-product-gallery__item--stub');
    if (stubs.length === 0) {
      window.Branch.productGallery.hydrationState = 'hydrated';
      return;
    }

    // Batch via DocumentFragment where possible — but since we're modifying slides in place
    // (they're already in the DOM tree), we just iterate. The DOM ops are innerHTML assignments
    // which are batched by the browser.
    for (var i = 0; i < stubs.length; i++) {
      hydrateStub(stubs[i]);
    }

    window.Branch.productGallery.hydrationState = 'hydrated';
    document.dispatchEvent(new CustomEvent('galleryHydrated'));
  }

  function hydrateStubsByMediaIds(mediaIds) {
    if (!mediaIds || !mediaIds.length) return;
    for (var i = 0; i < mediaIds.length; i++) {
      var stub = document.querySelector('.main-product-gallery__item--stub[data-media-id="' + mediaIds[i] + '"]');
      if (stub) hydrateStub(stub);
    }
    // If all stubs are now hydrated, mark state
    if (document.querySelectorAll('.main-product-gallery__item--stub').length === 0) {
      window.Branch.productGallery.hydrationState = 'hydrated';
      document.dispatchEvent(new CustomEvent('galleryHydrated'));
    }
  }

  // Expose public API
  window.Branch.productGallery.hydrateStubs = hydrateStubs;
  window.Branch.productGallery.hydrateStubsByMediaIds = hydrateStubsByMediaIds;

  // Schedule hydration as soon as possible after LCP paint.
  // Strategy: use PerformanceObserver for LCP if available; otherwise fall back to
  // requestAnimationFrame after DOMContentLoaded. Never wait longer than 1 second.
  function scheduleHydration() {
    var fired = false;
    function run() {
      if (fired) return;
      fired = true;
      // One rAF to get off the critical path, then hydrate
      requestAnimationFrame(function() {
        hydrateStubs();
      });
    }

    if ('PerformanceObserver' in window) {
      try {
        var po = new PerformanceObserver(function(list) {
          // As soon as any LCP entry is reported, schedule hydration
          if (list.getEntries().length > 0) {
            run();
            try { po.disconnect(); } catch (e) {}
          }
        });
        po.observe({ type: 'largest-contentful-paint', buffered: true });
      } catch (e) {
        // Ignore, fall through to other triggers
      }
    }

    // Fallback: hard timeout at 1s so we never miss hydration
    // (LCP observer above should fire first in normal conditions)
    setTimeout(run, 1000);
  }

  scheduleHydration();
})();
